import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { CartBrowsingCriteria } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { updateCartNameAndLastBC } from 'api/operations/cartServiceV2';
import { applyProposalUserAction, updateProposalName } from 'api/operations/enrollmentService';
import { updateGroup } from 'api/operations/groupService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { makeAutoObservable, runInAction } from 'mobx';
import { QuoteRouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import {
  DomainProductLineItem,
  DomainProductLinesDelegate,
} from 'store/modules/initiate-quote/domainProductLinesDelegate';
import { ChecklistCompletionStatus, QuoteChecklistCard, USER_ACTIONS } from 'types/enrollmentService';

export default class QuoteProposalInfoDelegate {
  private readonly _domainParams: QuoteFlowDomainParams;
  private _isActionLoading = false;
  public readonly flowDelegate: QuoteFlowDelegate;
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public get isStepCompleted(): boolean {
    if (this.flowDelegate.enrollmentPeriodModule.enrollmentProposalData === null) return false;

    return (
      this.flowDelegate.enrollmentPeriodModule.getProposalStepCompletionStatus(
        this._domainParams.cartId,
        QuoteChecklistCard.INITIATE_SHELF,
      ) === ChecklistCompletionStatus.COMPLETED
    );
  }

  public get isFlowReadyToComplete(): boolean {
    return !this._isActionLoading && !this.flowDelegate.isInitialDataLoading;
  }

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isDomainProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public getLocationFromCartOrGroup(): { zip: string; state: string; county: string } {
    const { zipCode, state, county } = this.flowDelegate.cartModule.lastUpdateBrowsingCriteria;
    const address = this.flowDelegate.groupModule.group.contactInfo.primaryAddress;

    return {
      zip: zipCode || address.zip || '',
      state: state || address.state || '',
      county: county || address.county || '',
    };
  }

  public async getInitialData(): Promise<void> {
    const cartId = this._domainParams.cartId;
    const cartModule = this.flowDelegate.cartModule;

    await this.flowDelegate.getFlowInitialData();
    await cartModule.getCart(cartId);
  }

  public async saveProposalInfo(proposalName: string, browsingCriteria: CartBrowsingCriteria): Promise<void> {
    const { cartId, periodId } = this._domainParams;

    this._setActionLoading(true);

    try {
      await Promise.all([
        updateProposalName(periodId, cartId, proposalName),
        updateGroup(buildInputGroupFromGroup(this.flowDelegate.groupModule.group)),
        updateCartNameAndLastBC(cartId, proposalName, browsingCriteria),
        ...(!this.isStepCompleted
          ? [applyProposalUserAction(periodId, cartId, USER_ACTIONS.COMPLETE_INITIATE_RFQ)]
          : []),
      ]);

      runInAction(() => {
        this._setActionLoading(false);
        this.flowDelegate.onCompleteRedirect();
      });
    } catch (error) {
      this._setActionLoading(false);
      toastManager.addToast('An error occurred while updating the proposal answers.', ToastColorTypes.WARNING);
    }
  }

  constructor(domainParams: QuoteFlowDomainParams) {
    this._domainParams = domainParams;
    this.flowDelegate = new QuoteFlowDelegate({
      domainParams,
      nextPageId: QuoteRouteV2Id.QUOTE_CHECKLIST,
    });
    // Domain availability only. Covered / in-force lines are locked on initiate quote, not here
    // (renewal edit must still Save when every GROUP line is already installed).
    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: 'GROUP',
    });

    makeAutoObservable(this, { flowDelegate: false, domainProductLinesDelegate: false }, { autoBind: true });
  }
}
